import { Injectable } from '@angular/core';
import { registerLocaleData } from "@angular/common";

// <Globalize & DevExtreme Localization Setup>
import Globalize from 'globalize';

import "devextreme/localization/globalize/number";
import "devextreme/localization/globalize/date";
import "devextreme/localization/globalize/currency";
import "devextreme/localization/globalize/message";

// Common and language-specific CLDR JSONs.
import supplementalCldrData from "src/assets/dev_languages/devextreme-cldr-data/supplemental.json";
import daCldrData from "src/assets/dev_languages/devextreme-cldr-data/da.json";
// </Globalize & DevExtreme Localization Setup>

// Constants for string literals.
const BROWSER_LANGUAGE = 'browserLanguage';
const USER_LANGUAGE = 'userLanguage';
const HYPHEN_SEPARATOR = '-';

/**
 * LocaleService is an Angular service that provides methods to detect and manage the user's preferred locale based on their browser settings.
 */
@Injectable({
	providedIn: "root",
})
export class LocaleService {
	/**
	 * Returns the user's preferred locale ID based on their browser settings.
	 * If no language information is available, it returns the provided defaultValue.
	 *
	 * @param {string} [defaultValue='en-US'] The default value to return if no locale is detected
	 * @returns {string} The detected locale ID or the default value
	 */
	public getCurrentLocaleId(defaultValue: string = 'en-US'): string {
		if (typeof navigator === 'undefined') {
			return defaultValue;
		}

		

		const browserLang: string = (navigator.languages && navigator.languages.length)
			? navigator.languages[0]
			: navigator.language || navigator[BROWSER_LANGUAGE] || navigator[USER_LANGUAGE];

		return browserLang || defaultValue;
	}

	/**
	 * Returns the user's preferred language code without the region, based on their browser settings.
	 * It calls getCurrentLocaleId and extracts the language code by splitting the locale ID at the hyphen separator.
	 *
	 * @param {string} [defaultValue='en'] The default value to return if no language code is detected
	 * @returns {string} The detected language code or the default value
	 */
	public getCurrentLocale(defaultValue: string = 'en'): string {
		let splitLanguageCountry = this.getCurrentLocaleId(defaultValue).split(HYPHEN_SEPARATOR);

		

		if (splitLanguageCountry && splitLanguageCountry[0]) {

			if (splitLanguageCountry[0] === 'da') {
				return splitLanguageCountry[0];
			}
		}

		return defaultValue;
	}

	/**
	 * Initializes the Globalize.js library with the specified locale.
	 * This function loads the necessary CLDR (Unicode Common Locale Data Repository) data for the given locale and sets the locale in Globalize.js.
	 *
	 * Globalize.js is used to format numbers, dates, currencies, and messages according to the user's locale settings.
	 *
	 * @param {string} locale - The locale to initialize in Globalize.js (e.g., 'en', 'da').
	 */
	public initializeGlobalize(locale: string) {
		Globalize.load(
			[
				supplementalCldrData,
				daCldrData
			]
		);
		Globalize.locale(locale);
	}
}

/**
 * This factory function returns the user's preferred locale by calling the getCurrentLocale method from the LocaleService instance.
 * It is used as a dependency injection provider for Angular's LOCALE_ID token.
 *
 * @param {LocaleService} localeService The LocaleService instance
 * @returns {string} The detected locale
 */
export function localeIdFactory(localeService: LocaleService): string {
	return localeService.getCurrentLocale();
}

/**
 * This function is an initializer that registers the appropriate locale data for the user's preferred locale and initializes Globalize.js with the same locale.
 * It waits for the DOMContentLoaded event, then dynamically imports the required locale data from the @angular/common/locales package and registers it using the registerLocaleData function.
 * It is used as a dependency injection provider for Angular's APP_INITIALIZER token.
 *
 * @param {LocaleService} localeService The LocaleService instance
 * @param {string} locale The user's preferred locale
 * @returns {() => Promise<void>} A function that returns a Promise that resolves once the locale data is registered
 */
export function localeInitializer(localeService: LocaleService, locale: string): () => Promise<void> {
	return async () => {
		await new Promise<void>(
			(resolve) => {
				document.addEventListener(
					'DOMContentLoaded',
					() => {
						resolve();
					}
				);
			}
		);

		// Initialize Globalize with the user's preferred locale.
		localeService.initializeGlobalize(locale);

		// Register Angular's locale data for the same locale.
		const module = await import(`node_modules/@angular/common/locales/${locale}.mjs`);
		registerLocaleData(module.default);
	};
}
